import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/ledgerApi";
import { Loader2, Phone, ArrowLeft, Trash2, Eye, MessageCircle, Download, X, Home, BadgeCheck, Banknote } from "lucide-react";
import { computePricing, IngredientsView, inr } from "@/components/ledger/BakeOrderBuilder";

const formatDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

function BakeCard({ order, onOpen, onDelete, confirming }) {
  const pricing = computePricing(order.total_cost, order.items, order.extra_pct ?? 100);
  const shown = order.items.slice(0, 4);
  return (
    <div
      data-testid={`bake-history-card-${order.id}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(order)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(order)}
      className="w-full cursor-pointer text-left bg-[var(--hoa-cream-50)] rounded-[var(--hoa-radius-lg)] shadow-[var(--hoa-shadow-card)] p-5 sm:p-6 hover:brightness-[1.03] transition-[filter]"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-[var(--hoa-ink-950)] text-lg sm:text-xl">
          {order.customer_name || "Unnamed bake"}
        </h2>
        <span className="flex items-center gap-3">
          <time className="font-label text-[9px] tracking-[0.18em] uppercase text-[rgba(11,12,13,0.5)]">
            {formatDate(order.created_at)}
          </time>
          <button
            data-testid={`bake-history-delete-${order.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(order);
            }}
            aria-label="Delete this order"
            className={`transition-colors ${
              confirming
                ? "text-[#E2543A] font-label text-[9px] tracking-[0.14em] uppercase"
                : "text-[rgba(11,12,13,0.35)] hover:text-[#B0432F]"
            }`}
          >
            {confirming ? "Sure?" : <Trash2 className="w-4 h-4" strokeWidth={1.8} />}
          </button>
        </span>
      </div>
      {order.customer_phone && (
        <p className="inline-flex items-center gap-1.5 font-body text-[rgba(11,12,13,0.65)] text-sm mt-1">
          <Phone className="w-3.5 h-3.5 text-[#8A6D33]" strokeWidth={1.8} /> {order.customer_phone}
        </p>
      )}
      <div className="mt-3 space-y-1">
        {shown.map((it) => (
          <p key={it.product_id} className="font-body text-[rgba(11,12,13,0.7)] text-sm leading-snug">
            <span className="text-[rgba(11,12,13,0.45)]">{it.category} · </span>
            {it.name} <span className="text-[#8A6D33]">× {it.qty}</span>
          </p>
        ))}
        {order.items.length > 4 && (
          <p className="font-body text-[rgba(11,12,13,0.45)] text-xs italic">+ {order.items.length - 4} more</p>
        )}
      </div>
      <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2 border-t border-[rgba(11,12,13,0.08)] pt-3">
        <span className="font-label text-[9px] tracking-[0.2em] uppercase text-[rgba(11,12,13,0.5)]">
          Making cost {inr(order.total_cost)}
        </span>
        <span className="font-display text-[#6B5426] text-lg">
          MRP {inr(pricing.mrp)}
          {order.payment_recorded && (
            <span
              data-testid={`bake-paid-chip-${order.id}`}
              className="ml-2 inline-flex items-center gap-1 align-middle rounded-full bg-[rgba(22,53,42,0.1)] text-[#16352a] font-label text-[8px] tracking-[0.16em] uppercase px-2 py-1"
            >
              <BadgeCheck className="w-3 h-3" strokeWidth={2} /> Paid
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

export const BakeHistory = ({ orders, loading, error, search, onRetry, onAuthError }) => {
  const [detail, setDetail] = useState(null);
  const [opening, setOpening] = useState("");
  const [invBusy, setInvBusy] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [confirmId, setConfirmId] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const invFileRef = useRef(null);

  const open = async (o) => {
    setOpening(o.id);
    try {
      const res = await api.get(`/admin/bake-orders/${o.id}`);
      invFileRef.current = null;
      setPayOpen(false);
      setDetail(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      if (onAuthError) onAuthError(e);
    } finally {
      setOpening("");
    }
  };

  const recordPayment = async (mode) => {
    setPayBusy(true);
    try {
      await api.post(`/admin/bake-orders/${detail.id}/record-payment`, { mode });
      setDetail((d) => ({ ...d, payment_recorded: true }));
      setPayOpen(false);
      onRetry();
    } catch (e) {
      if (e?.response?.status === 409) {
        setDetail((d) => ({ ...d, payment_recorded: true }));
        setPayOpen(false);
      } else if (onAuthError) onAuthError(e);
    } finally {
      setPayBusy(false);
    }
  };

  const remove = async (o) => {
    if (confirmId !== o.id) {
      setConfirmId(o.id);
      setTimeout(() => setConfirmId((c) => (c === o.id ? "" : c)), 4000);
      return;
    }
    setConfirmId("");
    try {
      await api.delete(`/admin/bake-orders/${o.id}`);
      onRetry();
    } catch (e) {
      if (onAuthError) onAuthError(e);
    }
  };

  const getInvoiceFile = async () => {
    if (invFileRef.current) return invFileRef.current;
    const res = await api.get(`/admin/bake-orders/${detail.id}/invoice`, { responseType: "blob" });
    const who = (detail.customer_name || detail.id.slice(0, 8)).trim().replace(/\s+/g, "-");
    const file = new File([res.data], `Invoice-${who}.png`, { type: "image/png" });
    invFileRef.current = file;
    return file;
  };

  const downloadFile = (file) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const viewInvoice = async () => {
    setInvBusy("view");
    try {
      const file = await getInvoiceFile();
      setPreviewUrl(URL.createObjectURL(file));
    } catch (e) {
      if (onAuthError) onAuthError(e);
    } finally {
      setInvBusy("");
    }
  };

  const whatsAppInvoice = async () => {
    setInvBusy("wa");
    try {
      const file = await getInvoiceFile();
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "house of appetite — invoice" });
          return;
        } catch (e) {
          if (e && e.name === "AbortError") return;
        }
      }
      downloadFile(file);
      window.open("https://wa.me/", "_blank", "noopener");
    } catch (e) {
      if (onAuthError) onAuthError(e);
    } finally {
      setInvBusy("");
    }
  };

  const downloadInvoice = async () => {
    setInvBusy("dl");
    try {
      downloadFile(await getInvoiceFile());
    } catch (e) {
      if (onAuthError) onAuthError(e);
    } finally {
      setInvBusy("");
    }
  };

  if (detail) {
    return (
      <div data-testid="bake-history-detail">
        <div className="flex flex-wrap items-center gap-4">
          <button
            data-testid="bake-history-back"
            onClick={() => setDetail(null)}
            className="inline-flex items-center gap-1.5 font-label text-[10px] tracking-[0.2em] uppercase text-[rgba(240,233,216,0.6)] hover:text-[var(--hoa-gold-400)] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} /> Back to Ledger
          </button>
          <Link
            to="/"
            data-testid="bake-history-home"
            className="inline-flex items-center gap-1.5 font-label text-[10px] tracking-[0.2em] uppercase text-[rgba(240,233,216,0.6)] hover:text-[var(--hoa-gold-400)] transition-colors"
          >
            <Home className="w-3.5 h-3.5" strokeWidth={1.8} /> Back to Homepage
          </Link>
        </div>
        <div className="mt-4">
          <h2 className="font-display text-[var(--hoa-cream-50)] text-2xl">{detail.customer_name || "Unnamed bake"}</h2>
          <p className="font-body text-[rgba(240,233,216,0.55)] text-sm mt-1">
            {detail.customer_phone ? `${detail.customer_phone} · ` : ""}
            {formatDate(detail.created_at)}
          </p>
        </div>
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <button
            data-testid="bake-invoice-view"
            onClick={viewInvoice}
            disabled={invBusy !== ""}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full border border-[rgba(179,145,74,0.45)] text-[var(--hoa-cream-50)] font-label text-[11px] tracking-[0.2em] uppercase hover:bg-[rgba(240,233,216,0.06)] transition-colors disabled:opacity-60"
          >
            {invBusy === "view" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" strokeWidth={1.8} />}
            View Invoice
          </button>
          <button
            data-testid="bake-invoice-whatsapp"
            onClick={whatsAppInvoice}
            disabled={invBusy !== ""}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-[#25D366] text-[#0B3D22] font-label text-[11px] tracking-[0.2em] uppercase shadow-[0_10px_30px_rgba(0,0,0,0.25)] hover:brightness-105 transition-[filter] disabled:opacity-60"
          >
            {invBusy === "wa" ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" strokeWidth={1.8} />}
            WhatsApp
          </button>
          <button
            data-testid="bake-invoice-download"
            onClick={downloadInvoice}
            disabled={invBusy !== ""}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)] font-label text-[11px] tracking-[0.2em] uppercase shadow-[0_10px_30px_rgba(0,0,0,0.25)] hover:brightness-110 transition-[filter] disabled:opacity-60"
          >
            {invBusy === "dl" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" strokeWidth={1.8} />}
            Download
          </button>
        </div>
        <div className="mt-4" data-testid="record-payment-section">
          {detail.payment_recorded ? (
            <span
              data-testid="payment-recorded-chip"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[rgba(143,191,159,0.12)] border border-[rgba(143,191,159,0.4)] text-[#8FBF9F] font-label text-[10px] tracking-[0.18em] uppercase"
            >
              <BadgeCheck className="w-4 h-4" strokeWidth={2} /> Payment recorded in the account book
            </span>
          ) : !payOpen ? (
            <button
              data-testid="record-payment-button"
              onClick={() => setPayOpen(true)}
              className="inline-flex items-center gap-2 h-12 px-7 rounded-full border border-[rgba(143,191,159,0.45)] text-[#8FBF9F] font-label text-[11px] tracking-[0.2em] uppercase hover:bg-[rgba(143,191,159,0.08)] transition-colors"
            >
              <Banknote className="w-4 h-4" strokeWidth={1.8} /> Record Payment
            </button>
          ) : (
            <div className="flex flex-wrap items-center gap-2" data-testid="record-payment-modes">
              <span className="font-label text-[9px] tracking-[0.18em] uppercase text-[rgba(240,233,216,0.5)] mr-1">
                Received via
              </span>
              {["Cash", "GPay", "Bank Transfer"].map((m) => (
                <button
                  key={m}
                  data-testid={`record-payment-mode-${m.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => recordPayment(m)}
                  disabled={payBusy}
                  className="h-10 px-5 rounded-full bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)] font-label text-[10px] tracking-[0.16em] uppercase hover:brightness-110 transition-[filter] disabled:opacity-50"
                >
                  {m}
                </button>
              ))}
              <button
                data-testid="record-payment-cancel"
                onClick={() => setPayOpen(false)}
                disabled={payBusy}
                className="h-10 px-4 rounded-full border border-[rgba(179,145,74,0.4)] text-[rgba(240,233,216,0.6)] font-label text-[9px] tracking-[0.16em] uppercase"
              >
                Cancel
              </button>
              {payBusy && <Loader2 className="w-4 h-4 animate-spin text-[var(--hoa-gold-400)]" />}
            </div>
          )}
        </div>
        {previewUrl && (
          <div
            data-testid="invoice-preview-overlay"
            className="fixed inset-0 z-50 bg-[rgba(0,0,0,0.75)] flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
            onClick={() => {
              URL.revokeObjectURL(previewUrl);
              setPreviewUrl(null);
            }}
          >
            <div className="relative w-full max-w-[520px]" onClick={(e) => e.stopPropagation()}>
              <button
                data-testid="invoice-preview-close"
                onClick={() => {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                }}
                aria-label="Close invoice preview"
                className="absolute -top-3 -right-3 z-10 w-10 h-10 rounded-full bg-[var(--hoa-gold-400)] text-[var(--hoa-ink-950)] flex items-center justify-center shadow-lg"
              >
                <X className="w-5 h-5" strokeWidth={2} />
              </button>
              <img src={previewUrl} alt="Invoice preview" className="w-full rounded-[12px] shadow-2xl" data-testid="invoice-preview-image" />
            </div>
          </div>
        )}
        <IngredientsView
          result={detail}
          onBack={() => setDetail(null)}
          onNew={() => setDetail(null)}
          extraPct={String(detail.extra_pct ?? 100)}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="bake-history-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--hoa-gold-400)]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-center py-20" data-testid="bake-history-error">
        <p className="font-body text-[rgba(240,233,216,0.7)]">The orders could not be loaded.</p>
        <button
          onClick={onRetry}
          data-testid="bake-history-retry"
          className="mt-5 font-label text-[11px] tracking-[0.18em] uppercase h-11 px-6 rounded-full border border-[rgba(179,145,74,0.45)] text-[var(--hoa-cream-50)] hover:bg-[rgba(240,233,216,0.06)] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const q = (search || "").trim().toLowerCase();
  const filtered = q
    ? orders.filter(
        (o) => (o.customer_name || "").toLowerCase().includes(q) || (o.customer_phone || "").includes(q)
      )
    : orders;

  if (filtered.length === 0) {
    return (
      <div
        className="text-center py-20 border border-dashed border-[rgba(179,145,74,0.35)] rounded-[var(--hoa-radius-lg)]"
        data-testid="bake-history-empty"
      >
        <p className="font-display text-[var(--hoa-cream-50)] text-xl">
          {q ? "No order matches that." : "No orders yet."}
        </p>
        <p className="font-body text-[rgba(240,233,216,0.55)] text-sm mt-2">
          {q ? "Try a different name or number." : "Orders you build with Add Order will appear here."}
        </p>
      </div>
    );
  }

  return (
    <>
      {opening && (
        <div className="flex items-center justify-center py-3">
          <Loader2 className="w-4 h-4 animate-spin text-[var(--hoa-gold-400)]" />
        </div>
      )}
      {filtered.map((o) => (
        <BakeCard key={o.id} order={o} onOpen={open} onDelete={remove} confirming={confirmId === o.id} />
      ))}
    </>
  );
};
